import React from 'react'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import CustomFieldInput from '../../../CustomFieldInput'
import AuthorSection from '../Details/AuthorSection'
import CreatedSection from '../Details/CreatedSection'
import UpdatedAtSection from '../Details/UpdatedAtSection'
import CustomFieldTable from '../../../CustomFieldTable'

import { isUUID, chunkArray } from '@SonarCX/utilities'
import { selectTaskMetaCache, loadMeta } from '@SonarCX/slices/taskMetaCache'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import CustomFieldFlow from '@SonarCX/components/molecules/CustomFieldFlow'
import useCustomFieldGroups from '@SonarCX/hooks/useCustomFieldGroups'
import useCustomFieldValues from '@SonarCX/hooks/useCustomFieldValues'
import useModalStyles from '../styles/TaskModal'
import useDetailsStyles from '../styles/TaskDetails'
import useStyles from './styles'

const TaskMetaSection = ({
  taskID,
  reset,
  updateParentMeta,
  values: childValues
}) => {
  const dispatch = useAppDispatch()

  const { loading, loaded, cache } = useAppSelector(selectTaskMetaCache)
  const { customFields } = useAppSelector(selectCustomFields)
  const { allUsers } = useUserAPI()
  const { currentUser } = useAgent()
  const { groups, hasFields } = useCustomFieldGroups('task', childValues.type)

  const [meta, setMeta] = React.useState([])
  const [metaRows, setMetaRows] = React.useState<APIMeta[]>([])
  const [shouldSave, setShouldSave] = React.useState(false)

  const { handleValueChange } = useCustomFieldValues({ meta, setMeta })

  const modalClasses = useModalStyles({
    canEdit: true,
    isCreatingTask: !Boolean(taskID)
  })
  const detailsClasses = useDetailsStyles()
  const classes = useStyles({ modal: true })

  // Loads meta
  React.useEffect(() => {
    if (!taskID) {
      return
    }

    if (!loading.includes(taskID) && !loaded.includes(taskID)) {
      dispatch(loadMeta(taskID))
    }
  }, [loading, loaded, taskID, dispatch])

  // Update fields from cache
  React.useEffect(() => {
    if (taskID in cache) {
      setMeta(cloneDeep(cache[taskID]))
    }
  }, [cache, taskID])

  // Resets values from parent
  React.useEffect(() => {
    if (reset) {
      setMeta([])
      setMetaRows([])
    }
  }, [reset])

  // Clean mock rows (meta rows) on data insert.
  React.useEffect(() => {
    if (metaRows.length === 0) {
      return
    }

    const tableIds = [...new Set(metaRows.map(mr => mr.key))]

    for (const tableId of tableIds) {
      const mockRows = metaRows.filter(x => x.key === tableId)
      const mockRowsRow = mockRows[0]?.row
      const tableOptions = customFields.find(field => field.id === tableId)

      if (
        meta
          .filter(
            x =>
              x.row === mockRowsRow &&
              x.key === tableId &&
              (tableOptions.options
                ? tableOptions.options.some(opt => opt === x.row_key)
                : (tableOptions.config?.options ?? []).some(
                    opt => opt === x.row_key
                  ))
          )
          .some(y => y.value !== '')
      ) {
        setMetaRows(old => {
          const cleanedRows = old.filter(o => o.key !== tableId)
          return cleanedRows
        })
      }
    }
  }, [meta, metaRows, customFields])

  // Saves meta after blur
  React.useEffect(() => {
    if (!shouldSave) {
      return
    }

    setShouldSave(false)

    // Only save values with UUID keys and convert empty strings to null
    const saveableMeta = meta
      .filter(obj => isUUID(obj.key))
      .map(({ key, value, row, row_key }) => {
        let val

        if (value instanceof Date) {
          val = value.toISOString()
        } else {
          val = value.toString().trim()
          if (val.length === 0) {
            val = null
          }
        }

        if (row_key) {
          return { key, value: val, row, row_key }
        }
        return { key, value: val }
      })

    // Save meta
    updateParentMeta && updateParentMeta(saveableMeta)
  }, [shouldSave, meta, taskID, dispatch, updateParentMeta])

  const handleBlur = React.useCallback(async () => {
    setShouldSave(true)
  }, [])

  if (!hasFields) {
    return null
  }

  return (
    <>
      {taskID && (
        <div
          className={modalClasses.bodyWrapper}
          style={{ marginBottom: '1.5rem' }}
        >
          <div className={clsx(classes.root, modalClasses.body)}>
            <section className={detailsClasses.taskInfo}>
              <div className={clsx(classes.infoColumn, classes.infoColumnGrid)}>
                <div
                  className={clsx(detailsClasses.gridItem, classes.specialItem)}
                >
                  <AuthorSection
                    classes={detailsClasses}
                    allUsers={allUsers}
                    currentUser={currentUser}
                    values={childValues}
                  />
                </div>

                <div
                  className={clsx(detailsClasses.gridItem, classes.specialItem)}
                >
                  <CreatedSection
                    classes={detailsClasses}
                    values={childValues}
                  />
                </div>

                <div
                  className={clsx(detailsClasses.gridItem, classes.specialItem)}
                >
                  <UpdatedAtSection
                    classes={detailsClasses}
                    values={childValues}
                  />
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      <div
        className={modalClasses.bodyWrapper}
        style={{ marginBottom: '0.5rem' }}
      >
        <div className={modalClasses.body}>
          {groups.map((group, i) => {
            const fields = group.field_ids
              .map(id => {
                const field = customFields.find(field => field.id === id)
                if (!field) {
                  return null
                }

                if (['table', 'flow'].includes(field.field_type)) {
                  return null
                }

                return (
                  <div key={field.id} className={detailsClasses.gridItem}>
                    <CustomFieldInput
                      field={field}
                      value={
                        meta.find(data => data.key === field.id)?.value ?? ''
                      }
                      disabled={false}
                      canEdit={true}
                      handleChange={handleValueChange}
                      handleBlur={handleBlur}
                      meta={meta}
                      task
                      shouldFilter
                    />
                  </div>
                )
              })
              .filter(Boolean)

            const tables = group.field_ids
              .map(id => {
                const field = customFields.find(field => field.id === id)
                if (!field) {
                  return null
                }

                if (field.field_type !== 'table') {
                  return null
                }

                return field
              })
              .filter(Boolean)

            const flows = group.field_ids
              .map(id => {
                const field = customFields.find(field => field.id === id)
                if (!field) {
                  return null
                }

                if (field.field_type !== 'flow') {
                  return null
                }

                return field
              })
              .filter(Boolean)

            const splitPoint = Math.ceil(fields.length / 2)
            const columns = chunkArray(fields, splitPoint)

            if (fields.length + tables.length + flows.length === 0) {
              return null
            }

            return (
              <React.Fragment key={`${group.id}-${i}`}>
                <div className={classes.group}>
                  <div className={classes.title}>
                    <span>{group.title.toLocaleUpperCase()}</span>
                    {group.description && <span>{group.description}</span>}
                  </div>

                  {fields.length > 0 && (
                    <div className={classes.gridContainer}>
                      {columns.map((column, i) =>
                        column.map((item, j) => (
                          <div
                            key={`${i}-${j}`}
                            style={{
                              gridColumn: `${i + 1} / ${i + 2}`,
                              gridRow: `${j + 1} / ${j + 2}`
                            }}
                          >
                            {item}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {tables.length > 0 && (
                    <div style={{ paddingBottom: 4, overflowY: 'auto' }}>
                      {tables.map(table => (
                        <CustomFieldTable
                          key={table.id}
                          table={table}
                          meta={meta}
                          setMeta={setMeta}
                          metaRows={metaRows}
                          setMetaRows={setMetaRows}
                          canEdit
                          handleValueChange={handleValueChange}
                          onBlur={handleBlur}
                        />
                      ))}
                    </div>
                  )}

                  {flows.length > 0 && (
                    <>
                      {flows.map(flow => {
                        return (
                          <CustomFieldFlow
                            meta={meta}
                            setMeta={setMeta}
                            key={flow.id}
                            flow={flow.config?.id}
                            canEdit
                            objectType={'task'}
                            handleChange={handleValueChange}
                            handleBlur={handleBlur}
                          />
                        )
                      })}
                    </>
                  )}
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default TaskMetaSection
